import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from "react";
import { jwtDecode } from "jwt-decode";
import axiosInstance, { API_ENDPOINTS } from "@/config/api";
import { useUser } from "@/context/userContext";
import {
    getRetryAfterSecondsFromHeaders,
    isRateLimitCoolingDown,
    setRateLimitCooldown,
} from "@/utils/rateLimit";
import { showFreeVotesNotification } from "@/utils/promoNotifications";
import { scheduleVoteTransactionRefresh, VOTE_HISTORY_CHANGED_EVENT } from "@/queries/scheduleVoteTransactionRefresh";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface BalanceContextType {
    balance: number;
    loading: boolean;
    error: string | null;
    updateBalance: (amount: number) => void;
    setBalanceDirect: (value: number) => void;
    reserveBalance: (requestId: string, amount: number) => boolean;
    settleBalanceReservation: (requestId: string, authoritativeBalance?: number) => void;
    releaseBalanceReservation: (requestId: string) => void;
    refreshBalance: () => Promise<void>;
    notifyVoteHistoryChanged: () => void;
}

const BalanceContext = createContext<BalanceContextType>({
    balance: 0,
    loading: false,
    error: null,
    updateBalance: () => { },
    setBalanceDirect: () => { },
    reserveBalance: () => false,
    settleBalanceReservation: () => { },
    releaseBalanceReservation: () => { },
    refreshBalance: async () => { },
    notifyVoteHistoryChanged: () => { },
});

const BALANCE_RATE_LIMIT_KEY = "votes:balance";

export const BalanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [balance, setBalance] = useState<number>(0);
    const authoritativeBalanceRef = useRef(0);
    const lastConfirmedBalanceRef = useRef<{ userId: string; balance: number } | null>(null);
    const balanceReservationsRef = useRef(new Map<string, number>());
    const { authToken, user, loading: userLoading, refreshAuth } = useUser();
    const nextRefreshAllowedAtRef = useRef(0);
    const queryClient = useQueryClient();

    const applyDisplayedBalance = useCallback(() => {
        const reserved = Array.from(balanceReservationsRef.current.values())
            .reduce((total, amount) => total + amount, 0);
        const displayed = Math.max(0, authoritativeBalanceRef.current - reserved);
        setBalance(displayed);
    }, []);

    const isAuthenticated = !!authToken && !!user;
    const token = authToken;
    const notifyVoteHistoryChanged = useCallback(() => {
        if (user?.id && authToken) scheduleVoteTransactionRefresh(queryClient, String(user.id));
    }, [authToken, queryClient, user?.id]);

    useEffect(() => {
        window.addEventListener(VOTE_HISTORY_CHANGED_EVENT, notifyVoteHistoryChanged);
        window.addEventListener("qit-session-ended", notifyVoteHistoryChanged);
        return () => {
            window.removeEventListener(VOTE_HISTORY_CHANGED_EVENT, notifyVoteHistoryChanged);
            window.removeEventListener("qit-session-ended", notifyVoteHistoryChanged);
        };
    }, [notifyVoteHistoryChanged]);


    // -------------------------------------------------------------
    // 🔐 Decode user_id from JWT
    // -------------------------------------------------------------
interface DecodedToken {
    user_id?: string;
}

    const getDecodedUserId = useCallback((jwt: string | null): string | null => {
        if (!jwt) return null;

        try {
            const decoded = jwtDecode<DecodedToken>(jwt);
            return decoded?.user_id ?? null;
        } catch (err) {
            console.warn("⚠️ Failed to decode JWT for balance fetch:", err);
            return null;
        }
    }, []);

    const realUserId = user?.id ?? getDecodedUserId(token) ?? null;

    const parseBalanceValue = useCallback((raw: unknown): number | null => {
        const candidates: unknown[] = [];

        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            const payload = raw as Record<string, unknown>;
            candidates.push(
                payload.balance,
                payload.amount,
                payload.currentBalance,
                payload.balance_after,
                payload.balanceAfter
            );
        }

        candidates.push(raw);

        for (const value of candidates) {
            if (typeof value === "number" && Number.isFinite(value)) {
                return Math.max(0, value);
            }
            if (typeof value === "string" && value.trim() !== "") {
                const parsed = Number(value);
                if (Number.isFinite(parsed)) {
                    return Math.max(0, parsed);
                }
            }
        }

        return null;
    }, []);

    const parseFreeBalanceValue = useCallback((raw: unknown): number | null => {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

        const payload = raw as Record<string, unknown>;
        const candidates = [
            payload.free_balance,
            payload.freeBalance,
            payload.free_votes,
            payload.freeVotes,
        ];

        for (const value of candidates) {
            if (typeof value === "number" && Number.isFinite(value)) {
                return Math.max(0, value);
            }
            if (typeof value === "string" && value.trim() !== "") {
                const parsed = Number(value);
                if (Number.isFinite(parsed)) {
                    return Math.max(0, parsed);
                }
            }
        }

        return null;
    }, []);

    const notifyFreeVotesCampaign = useCallback((raw: unknown) => {
        if (typeof window === "undefined") return;

        // Extract campaign details from the balance response if available.
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;

        const payload = raw as Record<string, unknown>;
        const freeBalance = parseFreeBalanceValue(raw);
        const campaignId = String(payload.recent_campaign_id || "");
        const campaignAmountValue = payload.recent_campaign_amount ?? payload.recentCampaignAmount;
        const campaignImageUrl = String(
            payload.recent_campaign_image_url ||
            payload.recentCampaignImageUrl ||
            ""
        ).trim();
        const campaignAmount = typeof campaignAmountValue === "number" && Number.isFinite(campaignAmountValue)
            ? Math.max(0, Math.floor(campaignAmountValue))
            : typeof campaignAmountValue === "string" && campaignAmountValue.trim() !== ""
                ? Math.max(0, Math.floor(Number(campaignAmountValue) || 0))
                : 0;
        
        // Only show notification if there's a campaign ID (admin grant)
        if (!campaignId) return;
        
        const storageKey = `qit:${campaignId}:notified`;
        if (window.localStorage.getItem(storageKey) === "true") return;

        window.localStorage.setItem(storageKey, "true");
        showFreeVotesNotification(campaignAmount || freeBalance || 10, "/home", campaignId, {
            imageUrl: campaignImageUrl || undefined,
        });
    }, [parseFreeBalanceValue]);

    const balanceQueryKey = useMemo(() => ["votes", "balance", realUserId] as const, [realUserId]);

    const fetchBalance = useCallback(async () => {
        if (isRateLimitCoolingDown(BALANCE_RATE_LIMIT_KEY)) {
            throw new Error("Balance refresh is temporarily rate limited");
        }

        try {
            const response = await axiosInstance.get(API_ENDPOINTS.votes.balance);
            if (String(response.headers?.["content-type"] || "").includes("text/html")) {
                throw new Error("Balance endpoint returned an invalid response");
            }
            if (parseBalanceValue(response.data) === null) throw new Error("Invalid balance data");
            return response.data as unknown;
        } catch (error: unknown) {
            const requestError = error as { response?: { status?: number; headers?: Record<string, string> } };
            if (requestError.response?.status === 429) {
                nextRefreshAllowedAtRef.current = setRateLimitCooldown(
                    BALANCE_RATE_LIMIT_KEY,
                    getRetryAfterSecondsFromHeaders(requestError.response.headers),
                    60,
                );
            }
            if (requestError.response?.status === 401 && await refreshAuth()) {
                const retry = await axiosInstance.get(API_ENDPOINTS.votes.balance);
                if (parseBalanceValue(retry.data) === null) throw new Error("Invalid balance data");
                return retry.data as unknown;
            }
            throw error;
        }
    }, [parseBalanceValue, refreshAuth]);

    const balanceQuery = useQuery({
        queryKey: balanceQueryKey,
        queryFn: fetchBalance,
        enabled: isAuthenticated && !!token && !!realUserId && !userLoading && balanceReservationsRef.current.size === 0,
        staleTime: 30_000,
        retry: false,
    });

    useEffect(() => {
        if (balanceQuery.data === undefined || balanceReservationsRef.current.size > 0) return;
        const nextBalance = parseBalanceValue(balanceQuery.data);
        if (nextBalance === null) return;
        const previous = lastConfirmedBalanceRef.current;
        const currentUserId = String(realUserId || "");
        if (previous?.userId === currentUserId && authoritativeBalanceRef.current !== nextBalance) notifyVoteHistoryChanged();
        lastConfirmedBalanceRef.current = { userId: currentUserId, balance: nextBalance };
        authoritativeBalanceRef.current = nextBalance;
        localStorage.setItem("userBalance", String(nextBalance));
        applyDisplayedBalance();
        notifyFreeVotesCampaign(balanceQuery.data);
    }, [applyDisplayedBalance, balanceQuery.data, notifyFreeVotesCampaign, parseBalanceValue, realUserId, notifyVoteHistoryChanged]);

    const refreshBalance = useCallback(async () => {
        if (!isAuthenticated || !token || !realUserId || userLoading || balanceReservationsRef.current.size > 0) return;
        if (Date.now() < nextRefreshAllowedAtRef.current || isRateLimitCoolingDown(BALANCE_RATE_LIMIT_KEY)) return;
        await queryClient.refetchQueries({ queryKey: balanceQueryKey, type: "active" });
    }, [balanceQueryKey, isAuthenticated, queryClient, realUserId, token, userLoading]);

    useEffect(() => {
        const resume = () => { void refreshBalance().catch(() => undefined); };
        window.addEventListener("qit:native-resume", resume);
        return () => window.removeEventListener("qit:native-resume", resume);
    }, [refreshBalance]);

    // -------------------------------------------------------------
    // 🔥 Load cached balance and auto-refresh - FIXED VERSION
    // -------------------------------------------------------------
    useEffect(() => {
        // Load cached balance on mount
        const cached = localStorage.getItem("userBalance");
        if (cached && !isNaN(Number(cached))) {
            const cachedBalance = Number(cached);
            console.log("💾 Loading cached balance:", cachedBalance);
            authoritativeBalanceRef.current = cachedBalance;
            applyDisplayedBalance();
        }
    }, [applyDisplayedBalance]);

    // -------------------------------------------------------------
    // 🎉 After Stripe PaymentSuccess → force refresh - FIXED VERSION
    // -------------------------------------------------------------
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentId = urlParams.get("payment_id");

        if (paymentId && isAuthenticated) {
            console.log("🎉 PaymentSuccess detected — refreshing balance in 2 seconds");

            // Longer delay to ensure payment is processed
            const timer = setTimeout(() => {
                refreshBalance();
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [isAuthenticated, refreshBalance]);

    // -------------------------------------------------------------
    // ➕ Utility functions
    // -------------------------------------------------------------
    const updateBalance = useCallback(
        (amount: number) => {
            const current = authoritativeBalanceRef.current;
            authoritativeBalanceRef.current = Math.max(0, current + amount);
            localStorage.setItem("userBalance", String(authoritativeBalanceRef.current));
            console.log("🔄 Updating balance:", { current, change: amount, new: authoritativeBalanceRef.current });
            applyDisplayedBalance();
        },
        [applyDisplayedBalance]
    );

    const setBalanceDirect = useCallback((value: number) => {
        console.log("🎯 Setting balance directly to:", value);
        authoritativeBalanceRef.current = Math.max(0, value);
        localStorage.setItem("userBalance", String(authoritativeBalanceRef.current));
        applyDisplayedBalance();
    }, [applyDisplayedBalance]);

    const reserveBalance = useCallback((requestId: string, amount: number): boolean => {
        const normalizedAmount = Math.max(0, Number(amount) || 0);
        if (!requestId || normalizedAmount <= 0 || balanceReservationsRef.current.has(requestId)) return false;
        const alreadyReserved = Array.from(balanceReservationsRef.current.values())
            .reduce((total, reservedAmount) => total + reservedAmount, 0);
        if (authoritativeBalanceRef.current - alreadyReserved < normalizedAmount) return false;
        // Prevent an older balance request from restoring votes already reserved.
        void queryClient.cancelQueries({ queryKey: balanceQueryKey, exact: true });
        balanceReservationsRef.current.set(requestId, normalizedAmount);
        applyDisplayedBalance();
        return true;
    }, [applyDisplayedBalance, balanceQueryKey, queryClient]);

    const settleBalanceReservation = useCallback((requestId: string, authoritativeBalance?: number) => {
        const reservedAmount = balanceReservationsRef.current.get(requestId) || 0;
        balanceReservationsRef.current.delete(requestId);
        // Commit this request's deduction exactly once. Other reservations
        // still represent deductions not yet committed to the local ledger.
        authoritativeBalanceRef.current = Math.max(0, authoritativeBalanceRef.current - reservedAmount);
        if (balanceReservationsRef.current.size === 0 &&
            typeof authoritativeBalance === "number" && Number.isFinite(authoritativeBalance)) {
            // Earlier concurrent responses may carry an older, higher balance.
            authoritativeBalanceRef.current = Math.min(authoritativeBalanceRef.current, Math.max(0, authoritativeBalance));
        }
        void queryClient.cancelQueries({ queryKey: balanceQueryKey, exact: true });
        // Keep the shared snapshot in sync so cached data cannot undo a vote.
        queryClient.setQueryData(balanceQueryKey, (previous: unknown) => ({
            ...(previous && typeof previous === "object" ? previous : {}),
            balance: authoritativeBalanceRef.current,
        }));
        localStorage.setItem("userBalance", String(authoritativeBalanceRef.current));
        applyDisplayedBalance();
        notifyVoteHistoryChanged();
    }, [applyDisplayedBalance, balanceQueryKey, notifyVoteHistoryChanged, queryClient]);

    const releaseBalanceReservation = useCallback((requestId: string) => {
        balanceReservationsRef.current.delete(requestId);
        applyDisplayedBalance();
    }, [applyDisplayedBalance]);

    const value = useMemo(
        () => ({
            balance,
            loading: balanceQuery.isFetching,
            error: balanceQuery.error ? "Failed to fetch balance" : null,
            updateBalance,
            setBalanceDirect,
            reserveBalance,
            settleBalanceReservation,
            releaseBalanceReservation,
            refreshBalance,
            notifyVoteHistoryChanged,
        }),
        [balance, balanceQuery.error, balanceQuery.isFetching, updateBalance, setBalanceDirect, reserveBalance, settleBalanceReservation, releaseBalanceReservation, refreshBalance, notifyVoteHistoryChanged]
    );

    return (
        <BalanceContext.Provider value={value}>
            {children}
        </BalanceContext.Provider>
    );
};

export const useBalance = () => useContext(BalanceContext);
